'use client';

import { FC, ImgHTMLAttributes, useLayoutEffect, useRef, useState } from 'react';

import { useIntersectionObserver } from '@core/Hooks/UseIntersectionObserver';
import cn from 'classnames';

import { ImageElementData, Ratio } from './ImageElement.types';
import { ImageUrlTransformationBuilder } from './ImageUrlTransformationBuilder';
import { createImageStyleWithRatio } from './Utils/CreateImageStyleWithRatio';

import styles from './ImageElement.module.css';

const IMG_WIDTHS = [
  64, 128, 240, 320, 480, 768, 960, 1024, 1280, 1440, 1920, 2048, 2160, 2304, 2560, 2880,
] as const;

type ImgWidths = (typeof IMG_WIDTHS)[number];

type Props = ImgHTMLAttributes<HTMLImageElement> & {
  imageElementData: ImageElementData;
  ratio: Ratio;
  initialWidth?: ImgWidths;
};

const ImageElement: FC<Props> = ({
  imageElementData,
  ratio,
  className,
  initialWidth = IMG_WIDTHS[0],
  ...imgProps
}) => {
  const $imageElementRef = useRef<HTMLDivElement>(null);
  const imageElementUrl = imageElementData.url;
  const imageElementAlt = imageElementData.description || '';
  const [imgWidth, setImgWidth] = useState(initialWidth);
  const initialImageUrl = new ImageUrlTransformationBuilder(imageElementUrl)
    .withWidth(initialWidth)
    .withAutomaticFormat()
    .getUrl();

  const [src, setSrc] = useState(initialImageUrl);
  const [isImageVisible, setIsImageVisible] = useState(false);

  useIntersectionObserver($imageElementRef, entry => {
    setIsImageVisible(isImageVisible => isImageVisible || entry.isIntersecting);
  });

  // TODO: add check for ssr
  useLayoutEffect(() => {
    const imageBuilder = new ImageUrlTransformationBuilder(imageElementUrl);
    imageBuilder.withAutomaticFormat();
    const handleResize = () => {
      window.requestAnimationFrame(() => {
        if (!$imageElementRef.current || !isImageVisible) {
          return;
        }
        const { width } = $imageElementRef.current.getBoundingClientRect();
        const newWidth = IMG_WIDTHS.find(w => width <= w) || IMG_WIDTHS[IMG_WIDTHS.length - 1];

        if (newWidth > imgWidth) {
          setImgWidth(newWidth);
          imageBuilder.withWidth(newWidth);
          setSrc(imageBuilder.getUrl());
        }
      });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageElementUrl, imgWidth, isImageVisible]);

  return (
    <div
      style={createImageStyleWithRatio(ratio)}
      ref={$imageElementRef}
      className={cn(styles.wrapper, className)}
    >
      <img src={src} alt={imageElementAlt} {...imgProps} />
    </div>
  );
};

export { ImageElement };
